import React, { useState } from 'react';
import { Import } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * ImportDeckDialog - Dialog for importing deck codes or JSON
 * shadcn/ui version
 */
export default function ImportDeckDialogShadcn({
  open,
  onOpenChange,
  onImport,
  onImportJson,
}) {
  const { t } = useLanguage();
  const [importCode, setImportCode] = useState('');
  const [importJson, setImportJson] = useState('');
  const [activeTab, setActiveTab] = useState('code');

  const handleImport = () => {
    if (activeTab === 'code' && importCode.trim()) {
      onImport(importCode.trim());
      setImportCode('');
      onOpenChange(false);
    } else if (activeTab === 'json' && importJson.trim()) {
      onImportJson(importJson.trim());
      setImportJson('');
      onOpenChange(false);
    }
  };

  const currentInput = activeTab === 'code' ? importCode : importJson;

  const handleOpenChange = (isOpen) => {
    onOpenChange(isOpen);
    if (!isOpen) {
      setImportCode('');
      setImportJson('');
      setActiveTab('code');
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t('controls.importDeckCode', 'Import Deck Code')}
          </DialogTitle>
          <DialogDescription>
            {t('deck.import.instructions', 'Paste your deck code below to import your deck.')}
          </DialogDescription>
        </DialogHeader>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="code">
              {t('deck.import.codeTab', 'Deck Code')}
            </TabsTrigger>
            <TabsTrigger value="json">
              {t('deck.import.jsonTab', 'JSON')}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="code" className="mt-3">
            <textarea
              className="w-full min-h-[120px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-y"
              value={importCode}
              onChange={(e) => setImportCode(e.target.value)}
              placeholder={t('deck.import.placeholder', 'Paste your deck code here...')}
            />
          </TabsContent>
          <TabsContent value="json" className="mt-3">
            <textarea
              className="w-full min-h-[120px] rounded-md border border-input bg-background px-3 py-2 text-sm font-mono ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-y"
              value={importJson}
              onChange={(e) => setImportJson(e.target.value)}
              placeholder={t('deck.import.jsonPlaceholder', '{\n  "version": 2,\n  "cards": [\n    { "number": "PR-052", "count": 2 }\n  ]\n}')}
            />
          </TabsContent>
        </Tabs>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={handleImport}
            disabled={!currentInput.trim()}
          >
            <Import className="h-4 w-4 mr-2" />
            {t('deck.import.importButton', 'Load Deck')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
